import { useState, useEffect } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { useConfirm } from '../contexts/ConfirmContext';
import Modal from '../components/Modal';
import Toast from '../components/Toast';

const EMPTY_FORM = { username: '', name: '', password: '', role: 'employee', branchId: '' };

const ROLES = [
  { value: 'employee', label: 'พนักงาน', badge: '#3498db' },
  { value: 'manager', label: 'ผู้จัดการ', badge: '#8e44ad' }
];
const roleOf = (r) => ROLES.find(x => x.value === r) || ROLES[0];

const when = (d) => (d ? new Date(d).toLocaleDateString('th-TH', { dateStyle: 'medium' }) : '-');

export default function EmployeeManagement({ apiUrl }) {
  const { branches, effectiveBranchId } = useBranch();
  const confirm = useConfirm();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM, branchId: effectiveBranchId ?? '' });
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม

  const token = () => localStorage.getItem('adminToken');

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token()}` }
      });
      const result = await response.json();
      if (response.ok) {
        setEmployees(result.data || []);
      } else {
        setMessage({ type: 'error', text: `โหลดรายชื่อไม่สำเร็จ: ${result.message || 'ไม่ทราบสาเหตุ'}` });
      }
    } catch (error) {
      console.error('Failed to load employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await fetch(`${apiUrl}/api/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: `เพิ่ม "${formData.name}" แล้ว` });
        setFormData({ ...EMPTY_FORM, branchId: effectiveBranchId ?? '' });
        setShowForm(false);
        loadEmployees();
      } else {
        setMessage({ type: 'error', text: result.message || 'เพิ่มพนักงานไม่สำเร็จ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด กรุณาลองใหม่' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (employee) => {
    const turningOff = employee.isActive;
    const verb = turningOff ? 'ปิดการใช้งาน' : 'เปิดการใช้งาน';
    if (turningOff && !await confirm(`เข้าระบบไม่ได้ทันที แต่ประวัติที่เคยทำไว้ยังอยู่ครบ`, { title: `${verb} "${employee.name}"?`, confirmText: verb, danger: true })) return;

    setBusyId(employee.id);
    try {
      const response = await fetch(`${apiUrl}/api/employees/${employee.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify({ isActive: !employee.isActive })
      });
      const result = await response.json();
      if (response.ok) { loadEmployees(); }
      else { setMessage({ type: 'error', text: result.message || `${verb}ไม่สำเร็จ` }); }
    } catch (error) {
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด' });
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleRole = async (employee) => {
    const next = employee.role === 'manager' ? 'employee' : 'manager';
    const label = roleOf(next).label;
    if (!await confirm(
      `${next === 'manager' ? 'จะซื้อเข้า ปรับปรุงสต็อก และปิดรอบนับได้' : 'จะทำได้แค่เบิกใช้ กรอกยอดนับ และดูประวัติ'}\n\nถ้าคนนี้ล็อกอินค้างอยู่ สิทธิ์เดิมยังใช้ได้จนครบ 8 ชั่วโมง`,
      { title: `เปลี่ยน "${employee.name}" เป็น ${label}?`, confirmText: 'เปลี่ยน' }
    )) return;

    setBusyId(employee.id);
    try {
      const response = await fetch(`${apiUrl}/api/employees/${employee.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify({ role: next })
      });
      const result = await response.json();
      if (response.ok) { loadEmployees(); }
      else { setMessage({ type: 'error', text: result.message || 'เปลี่ยนบทบาทไม่สำเร็จ' }); }
    } catch (error) {
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด' });
    } finally {
      setBusyId(null);
    }
  };

  const handleRename = async (employee) => {
    const name = prompt('ชื่อพนักงาน', employee.name);
    if (name === null || name.trim() === '' || name.trim() === employee.name) return;

    setBusyId(employee.id);
    try {
      const response = await fetch(`${apiUrl}/api/employees/${employee.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify({ name: name.trim() })
      });
      const result = await response.json();
      if (response.ok) { loadEmployees(); }
      else { setMessage({ type: 'error', text: result.message || 'เปลี่ยนชื่อไม่สำเร็จ' }); }
    } catch (error) {
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด' });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <div>กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  const active = employees.filter(e => e.isActive);
  const inactive = employees.filter(e => !e.isActive);

  return (
    <div>
      <Toast message={message} onDismiss={() => setMessage(null)} />
      <div className="page-header">
        <h2>👥 จัดการบุคลากร</h2>
        <button onClick={() => setShowForm(!showForm)} className="button">
          {showForm ? '❌ ยกเลิก' : '➕ เพิ่มพนักงาน'}
        </button>
      </div>

      {/* ตารางสิทธิ์ — เส้นแบ่งอยู่ที่ความเสี่ยงทางการเงิน ไม่ใช่ตำแหน่งในร้าน */}
      <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #3498db' }}>
        <p style={{ fontSize: '0.92rem', color: '#bbb', marginTop: 0 }}>
          บัญชีเหล่านี้ใช้เข้า<strong style={{ color: '#fff' }}>แอปพนักงาน</strong> —
          คนละบัญชีกับที่คุณใช้เข้าหน้าแอดมินนี้
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
          <div>
            <div style={{ color: '#3498db', fontWeight: 700, marginBottom: '0.3rem' }}>พนักงาน</div>
            <div style={{ fontSize: '0.88rem', color: '#bbb' }}>
              เบิกใช้ · กรอกยอดนับ · ดูประวัติ
            </div>
          </div>
          <div>
            <div style={{ color: '#8e44ad', fontWeight: 700, marginBottom: '0.3rem' }}>ผู้จัดการ</div>
            <div style={{ fontSize: '0.88rem', color: '#bbb' }}>
              ทั้งหมดข้างบน + ซื้อเข้า · ปรับปรุง · ปิดและยกเลิกรอบนับ
            </div>
          </div>
        </div>
        <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: 0, marginTop: '0.9rem' }}>
          สามอย่างหลังเป็นงานที่ทำให้ของหายจากระบบได้โดยไม่มีออเดอร์รองรับ จึงจำกัดไว้ที่ผู้จัดการ
        </p>
      </div>

      {/* ฟอร์มเพิ่ม */}
      <Modal open={showForm} onClose={() => { setShowForm(false); setFormData({ ...EMPTY_FORM, branchId: effectiveBranchId ?? '' }); }} title="เพิ่มพนักงานใหม่">
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              <div className="form-group">
                <label>ชื่อผู้ใช้ (สำหรับล็อกอิน) *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="เช่น somchai"
                  required
                />
                <small style={{ color: '#aaa', fontSize: '12px' }}>
                  a-z 0-9 . _ - เท่านั้น ห้ามเว้นวรรคและภาษาไทย
                </small>
              </div>

              <div className="form-group">
                <label>ชื่อที่แสดง *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="เช่น สมชาย"
                  required
                />
                <small style={{ color: '#aaa', fontSize: '12px' }}>
                  ชื่อนี้จะไปปรากฏในประวัติสต็อกว่าใครทำรายการ
                </small>
              </div>

              <div className="form-group">
                <label>รหัสผ่าน *</label>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="อย่างน้อย 6 ตัว"
                  required
                />
                <small style={{ color: '#aaa', fontSize: '12px' }}>
                  แสดงเป็นตัวอักษรปกติเพื่อให้จดไปบอกพนักงานได้
                </small>
              </div>

              <div className="form-group">
                <label>บทบาท *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <small style={{ color: '#aaa', fontSize: '12px' }}>
                  {formData.role === 'manager'
                    ? 'แอปพนักงาน: ทำได้ทุกอย่าง รวมซื้อเข้า ปรับปรุง และปิดรอบนับ · หน้าแอดมิน: เข้าได้ทุกหน้า (ยกเว้นจัดการสาขา)'
                    : 'แอปพนักงาน: เบิกใช้ กรอกยอดนับ และดูประวัติ · หน้าแอดมิน: เข้าได้เฉพาะจัดการโต๊ะ/ครัว/ชำระเงิน/เงินทอน'}
                </small>
              </div>

              <div className="form-group">
                <label>สาขา *</label>
                <select
                  value={formData.branchId}
                  onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                  required
                >
                  <option value="" disabled>-- เลือกสาขา --</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <small style={{ color: '#aaa', fontSize: '12px' }}>
                  พนักงานคนนี้จะเข้าระบบได้เฉพาะสาขานี้เท่านั้น
                </small>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => { setShowForm(false); setFormData({ ...EMPTY_FORM, branchId: effectiveBranchId ?? '' }); }}
                className="button"
                style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
              >
                ยกเลิก
              </button>
              <button type="submit" className="button" disabled={submitting}>
                {submitting ? '⏳ กำลังบันทึก...' : '💾 เพิ่มพนักงาน'}
              </button>
            </div>
          </form>
      </Modal>

      {/* รายชื่อ */}
      <div className="card">
        <h3 style={{ marginBottom: '1rem' }}>
          ใช้งานอยู่ ({active.length})
          {inactive.length > 0 && (
            <span style={{ color: '#888', fontWeight: 400, fontSize: '0.9rem' }}>
              {' '}· ปิดการใช้งาน {inactive.length}
            </span>
          )}
        </h3>

        <div style={{ overflowX: 'auto' }}>
          <table className="table-nowrap">
            <thead>
              <tr>
                <th>ชื่อ</th>
                <th>ชื่อผู้ใช้</th>
                <th>สาขา</th>
                <th>บทบาท</th>
                <th>สถานะ</th>
                <th>เพิ่มเมื่อ</th>
                <th>แอคชั่น</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => (
                <tr key={emp.id} style={emp.isActive ? undefined : { opacity: 0.5 }}>
                  <td style={{ fontWeight: 600 }}>{emp.name}</td>
                  <td style={{ fontFamily: 'monospace' }}>{emp.username}</td>
                  <td>{branches.find(b => b.id === emp.branchId)?.name || '-'}</td>
                  <td>
                    <span style={{
                      color: roleOf(emp.role).badge,
                      border: `1px solid ${roleOf(emp.role).badge}`,
                      borderRadius: '3px',
                      padding: '0.1rem 0.5rem',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}>
                      {roleOf(emp.role).label}
                    </span>
                  </td>
                  <td>
                    {emp.isActive
                      ? <span style={{ color: '#27ae60', fontWeight: 600 }}>✅ ใช้งานอยู่</span>
                      : <span style={{ color: '#888', fontWeight: 600 }}>⛔ ปิดอยู่</span>}
                  </td>
                  <td>{when(emp.createdAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleRename(emp)}
                        disabled={busyId === emp.id}
                        className="button"
                        style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem',
                                 background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' }}
                      >
                        ✏️ ชื่อ
                      </button>
                      <button
                        onClick={() => handleToggleRole(emp)}
                        disabled={busyId === emp.id}
                        className="button"
                        style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem',
                                 background: 'linear-gradient(135deg, #8e44ad 0%, #71368a 100%)' }}
                      >
                        {emp.role === 'manager' ? '⬇️ เป็นพนักงาน' : '⬆️ เป็นผู้จัดการ'}
                      </button>
                      <button
                        onClick={() => setResetTarget(emp)}
                        disabled={busyId === emp.id}
                        className="button"
                        style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem',
                                 background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' }}
                      >
                        🔑 รหัสผ่าน
                      </button>
                      <button
                        onClick={() => handleToggleActive(emp)}
                        disabled={busyId === emp.id}
                        className="button"
                        style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem',
                                 background: emp.isActive
                                   ? 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)'
                                   : 'linear-gradient(135deg, #27ae60 0%, #229954 100%)' }}
                      >
                        {emp.isActive ? '⛔ ปิด' : '✅ เปิด'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {employees.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              ยังไม่มีพนักงานในระบบ
            </div>
          )}
        </div>

        <p style={{ fontSize: '0.85rem', color: '#888', marginTop: '1rem' }}>
          ไม่มีปุ่มลบถาวรโดยตั้งใจ — ชื่อพนักงานถูกอ้างอิงอยู่ในประวัติสต็อกและรอบเดินสต็อก
          ลบทิ้งแล้วจะตรวจสอบย้อนหลังไม่ได้ว่าใครทำรายการ ใช้ปิดการใช้งานแทน
        </p>
      </div>

      {resetTarget && (
        <ResetPasswordModal
          employee={resetTarget}
          apiUrl={apiUrl}
          onClose={() => setResetTarget(null)}
          onDone={() => { setResetTarget(null); loadEmployees(); }}
          setMessage={setMessage}
        />
      )}
    </div>
  );
}

function ResetPasswordModal({ employee, apiUrl, onClose, onDone, setMessage }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await fetch(`${apiUrl}/api/employees/${employee.id}/password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        },
        body: JSON.stringify({ password })
      });
      const result = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: `เปลี่ยนรหัสผ่านของ "${employee.name}" แล้ว` });
        onDone();
      } else {
        setMessage({ type: 'error', text: result.message || 'เปลี่ยนรหัสผ่านไม่สำเร็จ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="🔑 ตั้งรหัสผ่านใหม่">
        <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '1rem' }}>
          {employee.name} ({employee.username})
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>รหัสผ่านใหม่ *</label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="อย่างน้อย 6 ตัว"
              required
              autoFocus
            />
          </div>

          <p style={{ fontSize: '0.85rem', color: '#f39c12' }}>
            ถ้าคนนี้ล็อกอินค้างอยู่ในแอปพนักงาน จะยังใช้ต่อได้จนครบ 8 ชั่วโมง
            ถ้าต้องตัดทันทีให้กดปิดการใช้งานแทน
          </p>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              className="button"
              style={{ background: 'linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%)' }}
            >
              ยกเลิก
            </button>
            <button type="submit" className="button" disabled={submitting}>
              {submitting ? '⏳ กำลังบันทึก...' : '💾 บันทึก'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
